import { createApi } from '@reduxjs/toolkit/query/react'
import { createBaseQueryWithReauth } from 'store/auth/base-query-with-reauth'
import {
  DocumentPresignedUrlResponse,
  EntityDocument,
  ListDocumentsPayload,
  UploadDocumentPayload,
} from './documents.types'

const baseUrl = `${process.env.REACT_APP_ONBOARDING_SERVICE_API}`

export const documentsApi = createApi({
  reducerPath: 'documentsApi',
  baseQuery: createBaseQueryWithReauth(baseUrl!),
  tagTypes: ['Documents'],
  endpoints: (builder) => ({
    listDocuments: builder.query<EntityDocument[], ListDocumentsPayload>({
      query: ({ associateId, internalType }) => ({
        url: 'oapi/v1/documents',
        params: { associateId, internalType },
      }),
      providesTags: (_result, _error, { associateId, internalType }) => [
        { type: 'Documents', id: `${associateId}-${internalType}` },
      ],
    }),

    uploadDocument: builder.mutation<EntityDocument, UploadDocumentPayload>({
      query: ({ file, associateId, internalType, documentType, expiryDate }) => {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('associateId', associateId)
        formData.append('internalType', internalType)
        formData.append('documentType', documentType)
        if (expiryDate) {
          formData.append('expiryDate', expiryDate)
        }
        return {
          url: 'oapi/v1/documents/upload',
          method: 'POST',
          body: formData,
        }
      },
      invalidatesTags: (_result, _error, { associateId, internalType }) => [
        { type: 'Documents', id: `${associateId}-${internalType}` },
      ],
    }),

    deleteDocument: builder.mutation<void, { documentId: string; associateId: string; internalType: ListDocumentsPayload['internalType'] }>({
      query: ({ documentId }) => ({
        url: `oapi/v1/documents/${documentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { associateId, internalType }) => [
        { type: 'Documents', id: `${associateId}-${internalType}` },
      ],
    }),

    getDocumentPresignedUrl: builder.mutation<DocumentPresignedUrlResponse, string>({
      query: (documentId) => ({
        url: `oapi/v1/documents/${documentId}/presigned-url`,
        method: 'GET',
      }),
    }),
  }),
})

export const {
  useListDocumentsQuery,
  useUploadDocumentMutation,
  useDeleteDocumentMutation,
  useGetDocumentPresignedUrlMutation,
} = documentsApi
